import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { 
  Bot, Send, Sparkles, Layers, Plus, 
  Menu, X, Search, Trash2, MessageCircle,
  Copy, Check
} from 'lucide-react'
import { format } from 'date-fns'
import ReactMarkdown from 'react-markdown'
import type { Message, Conversation } from '../types'

// Message Component
function MessageBubble({ message, isStreaming = false }: { message: { id: string; role: string; content: string; timestamp: string; provider?: string }; isStreaming?: boolean }) {
  const isUser = message.role === 'user'
  const [copied, setCopied] = useState(false)

  const copyToClipboard = () => {
    navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`flex gap-4 ${isUser ? 'flex-row-reverse' : ''} animate-fade-in`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-dark-600' : 'bg-primary-500'
      }`}>
        {isUser ? (
          <span className="text-sm font-medium text-dark-200">U</span>
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>

      {/* Message */}
      <div className={`flex-1 max-w-3xl ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block rounded-2xl px-4 py-3 text-sm ${
          isUser 
            ? 'bg-primary-500 text-white rounded-br-md' 
            : 'bg-dark-800 text-dark-100 rounded-bl-md'
        }`}>
          <div className="prose prose-invert prose-sm max-w-none">
            <ReactMarkdown
              components={{
                code({ node, inline, className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || '')
                  return !inline ? (
                    <div className="relative group my-4">
                      <div className="flex items-center justify-between bg-dark-900 px-4 py-2 rounded-t-lg border border-dark-700">
                        <span className="text-xs text-dark-400">{match?.[1] || 'code'}</span>
                        <button 
                          onClick={copyToClipboard}
                          className="p-1 hover:bg-dark-700 rounded transition-colors"
                        >
                          {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3 text-dark-400" />}
                        </button>
                      </div>
                      <pre className="bg-dark-900 px-4 py-3 rounded-b-lg overflow-x-auto border-x border-b border-dark-700">
                        <code className={className} {...props}>{children}</code>
                      </pre>
                    </div>
                  ) : (
                    <code className={className} {...props}>{children}</code>
                  )
                }
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
        
        {/* Meta */}
        <div className={`flex items-center gap-2 mt-1 text-xs text-dark-500 ${isUser ? 'justify-end' : ''}`}>
          <span>{format(new Date(message.timestamp), 'HH:mm')}</span>
          {!isUser && message.provider && (
            <span className="px-1.5 py-0.5 bg-dark-700 rounded text-dark-300 uppercase">
              {message.provider}
            </span>
          )}
          {isStreaming && (
            <span className="text-primary-400 animate-pulse">streaming...</span>
          )}
        </div>
      </div>
    </div>
  )
}

// Conversation Item Component
function ConversationItem({ 
  conversation, 
  isActive, 
  onClick, 
  onDelete 
}: { 
  conversation: { id: string; title: string; mode: string; updatedAt: string }
  isActive: boolean
  onClick: () => void
  onDelete: (e: React.MouseEvent) => void
}) {
  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'agentic': return <Sparkles className="w-3 h-3" />
      case 'multi-agent': return <Layers className="w-3 h-3" />
      default: return <MessageCircle className="w-3 h-3" />
    }
  }

  return (
    <div 
      onClick={onClick}
      className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
        isActive ? 'bg-dark-700' : 'hover:bg-dark-800'
      }`}
    >
      <div className="text-dark-400">{getModeIcon(conversation.mode)}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-dark-200 truncate">{conversation.title}</p>
        <p className="text-xs text-dark-500">{format(new Date(conversation.updatedAt), 'MMM d, HH:mm')}</p>
      </div>
      <button 
        onClick={onDelete}
        className="p-1.5 text-dark-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// Main Chat Page
export default function ChatPage() {
  const { user } = useAuthStore()
  const {
    conversations,
    currentConversation,
    messages,
    isStreaming,
    streamingContent,
    chatMode,
    isSidebarOpen,
    searchQuery,
    fetchConversations,
    createConversation,
    selectConversation,
    deleteConversation,
    sendMessage,
    cancelStreaming,
    setChatMode,
    toggleSidebar,
    setSearchQuery,
    clearCurrentConversation
  } = useChatStore()

  const [message, setMessage] = useState('')
  const [inputKey, setInputKey] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Fetch conversations on mount
  useEffect(() => {
    if (user?.id) {
      fetchConversations(user.id)
    }
  }, [user?.id])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingContent])

  const handleSend = async () => {
    if (!message.trim() || isStreaming) return

    const userId = user?.id
    if (!userId) return

    await sendMessage(userId, message.trim())
    setMessage('')
    setInputKey(Date.now().toString())
  }

  const handleNewChat = async () => {
    const userId = user?.id
    if (!userId) return

    clearCurrentConversation()
    await createConversation(userId, 'New Chat', chatMode)
  }

  const filteredConversations = conversations.filter(c => 
    c.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="h-full flex bg-dark-900">
      {/* Sidebar */}
      <div className={`${isSidebarOpen ? 'w-72' : 'w-0'} flex-shrink-0 border-r border-dark-700 flex flex-col transition-all duration-300 overflow-hidden`}>
        {/* Header */}
        <div className="p-4 border-b border-dark-700">
          <button 
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>

        {/* Search */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-dark-800 border border-dark-600 rounded-lg pl-9 pr-3 py-2 text-sm text-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredConversations.length === 0 ? (
            <div className="text-center text-dark-500 text-sm py-8">
              No conversations yet
            </div>
          ) : (
            <div className="space-y-1">
              {filteredConversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={currentConversation?.id === conv.id}
                  onClick={() => selectConversation(conv)}
                  onDelete={(e) => {
                    e.stopPropagation()
                    deleteConversation(conv.id)
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-dark-700">
          <button 
            onClick={toggleSidebar}
            className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
          >
            {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Mode Selector */}
          <div className="flex items-center gap-1 p-1 bg-dark-800 rounded-lg">
            <button
              onClick={() => setChatMode('assistant')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chatMode === 'assistant' 
                  ? 'bg-primary-500/10 text-primary-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              <Bot className="w-4 h-4" />
              Assistant
            </button>
            <button
              onClick={() => setChatMode('agentic')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chatMode === 'agentic' 
                  ? 'bg-primary-500/10 text-primary-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Agentic
            </button>
            <button
              onClick={() => setChatMode('multi-agent')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chatMode === 'multi-agent' 
                  ? 'bg-primary-500/10 text-primary-400' 
                  : 'text-dark-400 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4" />
              Multi-Agent
            </button>
          </div>

          {/* New Chat Button (Mobile) */}
          <button 
            onClick={handleNewChat}
            className="ml-auto p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors lg:hidden"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {messages.length === 0 && !isStreaming ? (
            /* Welcome Message */
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 max-w-2xl">
                <div className="bg-dark-800 rounded-xl p-5">
                  <p className="text-dark-200 mb-4">
                    👋 Hi {user?.email?.split('@')[0]}! I'm HalCode, your AI coding assistant.
                  </p>
                  <p className="text-dark-300 mb-4">
                    I can help you with:
                  </p>
                  <ul className="space-y-2 text-dark-300">
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Writing and editing code
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Explaining code and concepts
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Debugging issues
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Refactoring and optimizing
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-primary-400">→</span> Agentic workflows for complex tasks
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              
              {isStreaming && streamingContent && (
                <MessageBubble 
                  message={{
                    id: 'streaming',
                    role: 'assistant',
                    content: streamingContent,
                    timestamp: new Date().toISOString()
                  }} 
                  isStreaming
                />
              )}
            </>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-dark-700">
          <div className="flex items-end gap-2 max-w-4xl mx-auto">
            <button className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors">
              <Plus className="w-5 h-5" />
            </button>
            
            <div className="flex-1 relative">
              <textarea
                key={inputKey}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder="Ask HalCode anything..."
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-3 pr-12 text-dark-100 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none max-h-48"
                rows={1}
                style={{ minHeight: '48px' }}
              />
              
              <div className="absolute right-2 bottom-2 flex items-center gap-1">
                {isStreaming && (
                  <button 
                    onClick={cancelStreaming}
                    className="p-1.5 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleSend}
                  disabled={!message.trim() || isStreaming}
                  className="p-1.5 text-primary-400 hover:text-primary-300 disabled:text-dark-500 transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-dark-500 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  )
}
